// Pure restore/resync helpers extracted from GameBoard. Each transforms a saved
// session blob (or part of it) into render-ready values with no scene or
// component-instance dependency, so they unit-test in isolation.

// A populated firstPlayerSeat proves the roll resolved, so matchPhase must be
// 'playing' regardless of a stale 'rollOff' left in the blob (defends against
// autoSave write races and any path that failed to flip the phase).
export function resolveRestoredMatchPhase(session) {
  const rs = session && session.firstPlayerRollState;
  const rollResolved = rs && (rs.firstPlayerSeat === 1 || rs.firstPlayerSeat === 2);
  return (session && session.matchPhase === 'rollOff' && !rollResolved) ? 'rollOff' : 'playing';
}

// Clamp a stored first-player roll state to valid values (numbers for rolls,
// seat 1/2 or null), tolerating missing or malformed input.
export function normalizeRollState(rollState) {
  const rs = (rollState && typeof rollState === 'object') ? rollState : {};
  const num = (v) => (typeof v === 'number' ? v : null);
  const seat = (v) => (v === 1 || v === 2 ? v : null);
  return {
    hostRoll: num(rs.hostRoll),
    guestRoll: num(rs.guestRoll),
    winnerSeat: seat(rs.winnerSeat),
    firstPlayerSeat: seat(rs.firstPlayerSeat),
  };
}

// Keep only well-formed prompt descriptors (plain objects with id + kind) so a
// corrupt blob can't feed garbage into the prompt-stack rehydrate.
export function sanitizePromptStack(stack) {
  return Array.isArray(stack)
    ? stack.filter((d) => d && typeof d === 'object' && d.id && d.kind)
    : [];
}

// Rehydrate stored game-log entries, turning numeric timestamps back into Date
// objects and defaulting missing times to now.
export function rehydrateGameLog(log) {
  return (log || []).map((e) => ({
    ...e,
    time: typeof e.time === 'number' ? new Date(e.time) : (e.time || new Date()),
  }));
}

// Whether the first-player dice modal may auto-open. It must only do so for a
// genuinely fresh match. Bail when the roll already resolved (firstPlayerSeat
// set) or the game is underway (gameStarted). Re-opening would clobber a
// restored board back to 'rollOff'. ALSO bail while a durable authoritative
// snapshot is still in flight (authSnapshotPending): a reconnect/app-restart
// requests the server save, and arming the dice on the 1s mount timer before
// it lands is the reload "stuck on the initiative modal + wiped board" bug.
// The snapshot's arrival (restore) or a 'none' reply (truly fresh) releases the
// gate and re-runs this check.
export function shouldArmFirstPlayerDice({ gameStarted, firstPlayerSeat, authSnapshotPending } = {}) {
  if (gameStarted) return false;
  if (firstPlayerSeat === 1 || firstPlayerSeat === 2) return false;
  if (authSnapshotPending) return false;
  return true;
}

